import ShopApi from '@apis/shop/ShopApi';
import useTestUser from '@hooks/test/useTestUser';
import useToast from '@hooks/ui/useToast';
import useDebouncedValue from '@hooks/useDebouncedValue';
import { GetShopsInMapParams } from '@models/shop';
import { ShopOrderStatus } from '@models/shop/common';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import {
    isMyShopAtom,
    locationAgreedAtom,
    neLatAtom,
    neLonAtom,
    queryAtom,
    swLatAtom,
    swLonAtom,
    userLatLonAtom,
} from '../../../atoms/shopsInMapAtoms';

interface Props {
  originalItemId?: string,
  isOrder?: boolean
}

function useShopsInMap({ originalItemId, isOrder }:Props) {
    const neLat = useAtomValue(neLatAtom);
    const neLon = useAtomValue(neLonAtom);
    const swLat = useAtomValue(swLatAtom);
    const swLon = useAtomValue(swLonAtom);
    const { lat: userLat, lon: userLon } = useAtomValue(userLatLonAtom);
    const query = useAtomValue(queryAtom);
    const debouncedQuery = useDebouncedValue(query);
    const myShop = useAtomValue(isMyShopAtom);
    const locationAgreed = useAtomValue(locationAgreedAtom);
    const params: GetShopsInMapParams = {
        neLat,
        neLon,
        swLat,
        swLon,
        userLat,
        userLon,
        query: debouncedQuery,
        myShop,
        originalItemId,
        locationAgreed,
        searchType: isOrder ? 'ORDER' : 'SEARCH_SHOP',
    };
    const enabled = !!neLat && !!neLon && !!swLat && !!swLon;
    const { data, status } = useQuery({
        queryKey: ['shopsInMap', params],
        queryFn: () => ShopApi.getShopsInMap(params),
        enabled,
        placeholderData: (prev) => prev,
    });
    const toast = useToast();
    const isTestUser = useTestUser();
    const shops = data
        ?.filter((shop) => isTestUser || !shop?.isTestShop);

    useEffect(() => {
        if (status === 'error') {
            toast.error('매장 조회 중 오류가 발생하였습니다.');
        }
    }, [status, toast]);
    const markers: MarkerProps[]|undefined = shops
        ?.map((shop) => {
            const {
                lat, lon, name, isMyShop, id,
            } = shop;
            return {
                lat, lng: lon, name, isMyShop, id,
            };
        });
    return { markers, shops, status };
}

export default useShopsInMap;
